import { useCallback, useEffect, useRef, useState } from 'react';
import { Streams } from '../api/client';
import { StreamChannel, StreamFrame } from '../types';

export type StreamStatus = 'off' | 'connecting' | 'open' | 'reconnecting' | 'paused' | 'ended' | 'error';

const MAX_ATTEMPTS = 8;
/** The server pings every 15 s; silence this long means the socket is dead. */
const WATCHDOG_MS = 45000;
/** A hidden tab keeps its stream for a minute, then lets go until it is visible again. */
const HIDDEN_CLOSE_MS = 60000;
/** Close codes the server uses for refusals that will not change by retrying. */
const PERMANENT_CLOSE_CODES = new Set([4400, 4401, 4403, 4404]);

const CLOSE_MESSAGES: Record<number, string> = {
  4400: 'The stream request was rejected.',
  4401: 'Your session has expired. Reload the page to sign in again.',
  4403: 'Only admins and developers can open world streams.',
  4404: 'This world no longer exists.',
};

/**
 * Keeps one WebSocket open to a world stream (console or resource usage) while `enabled`, delivering
 * every frame to `onFrame`. Reconnects with backoff after a dropped connection, never after a
 * refusal or a stream that ended (call `retry` for that), and pauses while the tab stays hidden.
 */
export function useWorldStream(
  worldId: string,
  channel: StreamChannel,
  enabled: boolean,
  onFrame: (frame: StreamFrame) => void
): { status: StreamStatus; error: string | null; endReason: string | null; retry: () => void } {
  const [status, setStatus] = useState<StreamStatus>('off');
  const [error, setError] = useState<string | null>(null);
  const [endReason, setEndReason] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;

  useEffect(() => {
    if (!enabled) {
      setStatus('off');
      return;
    }
    let ws: WebSocket | null = null;
    let attempts = 0;
    let stopped = false;
    let permanent = false;
    let reconnectTimer: number | undefined;
    let watchdogTimer: number | undefined;
    let hiddenTimer: number | undefined;
    setError(null);
    setEndReason(null);

    const armWatchdog = () => {
      window.clearTimeout(watchdogTimer);
      watchdogTimer = window.setTimeout(() => {
        // Nothing for a while: drop the socket so the close handler reconnects.
        if (ws) ws.close(4000, 'stale');
      }, WATCHDOG_MS);
    };

    const scheduleReconnect = () => {
      if (stopped || permanent) return;
      if (attempts >= MAX_ATTEMPTS) {
        setStatus('error');
        setError('Lost the connection to the control service.');
        return;
      }
      const delay = Math.min(30000, 1000 * 2 ** attempts) + Math.random() * 500;
      attempts += 1;
      setStatus('reconnecting');
      reconnectTimer = window.setTimeout(connect, delay);
    };

    const connect = () => {
      if (stopped || permanent) return;
      setStatus(attempts === 0 ? 'connecting' : 'reconnecting');
      const socket = new WebSocket(Streams.url(worldId, channel));
      ws = socket;
      socket.onopen = () => {
        attempts = 0;
        setStatus('open');
        setError(null);
        armWatchdog();
      };
      socket.onmessage = (ev: MessageEvent) => {
        armWatchdog();
        let frame: StreamFrame;
        try {
          frame = JSON.parse(String(ev.data)) as StreamFrame;
        } catch {
          return;
        }
        if (frame.type === 'ping') return;
        if (frame.type === 'error') {
          setError(frame.message);
          if (frame.permanent) {
            permanent = true;
            setStatus('error');
          }
        } else if (frame.type === 'end') {
          permanent = true;
          setEndReason(frame.reason);
          setStatus('ended');
        }
        onFrameRef.current(frame);
      };
      socket.onclose = (ev: CloseEvent) => {
        if (ws !== socket) return; // closed on purpose (leaving, hidden tab, retry)
        ws = null;
        window.clearTimeout(watchdogTimer);
        if (stopped || permanent) return;
        if (PERMANENT_CLOSE_CODES.has(ev.code)) {
          permanent = true;
          setStatus('error');
          setError((prev) => prev || CLOSE_MESSAGES[ev.code] || 'The stream was refused.');
          return;
        }
        scheduleReconnect();
      };
      socket.onerror = () => {
        // The close event that follows drives the reconnect.
      };
    };

    const onVisibility = () => {
      if (document.hidden) {
        hiddenTimer = window.setTimeout(() => {
          if (ws) {
            const s = ws;
            ws = null;
            s.close(1000, 'hidden');
            setStatus('paused');
          }
        }, HIDDEN_CLOSE_MS);
      } else {
        window.clearTimeout(hiddenTimer);
        if (!ws && !permanent && !stopped) {
          window.clearTimeout(reconnectTimer);
          attempts = 0;
          connect();
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    connect();

    return () => {
      stopped = true;
      window.clearTimeout(reconnectTimer);
      window.clearTimeout(watchdogTimer);
      window.clearTimeout(hiddenTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      if (ws) {
        const s = ws;
        ws = null;
        s.close(1000, 'leaving');
      }
    };
  }, [worldId, channel, enabled, epoch]);

  const retry = useCallback(() => setEpoch((e) => e + 1), []);
  return { status, error, endReason, retry };
}
